import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Button,
    TextField,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    useTheme,
    Chip,
    CircularProgress,
    Card,
    CardContent,
    Typography,
    Divider
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Add, School } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Field, FormikProvider, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import {
    initialValues,
    validationSchema
} from '../Formik/courseOverviewformikData';
import {
    createCourse,
    editCourse,
    uploadThumbnail,
    getIndividualCourse,
    getAllCategories,
    getSubCategoriesById
} from '../../../../API/Course';
import BreadCrumb from '../../../../common/components/Breadcrumb';
import 'react-quill/dist/quill.snow.css';
import { setCurrentScreenLoading } from '../Slices/currentScreen';
import { store } from '../../../../store';

const breadcrumb = [
    {
        id: '01',
        breadItem: 'Course'
    },
    {
        id: '02',
        breadItem: 'Course Overview'
    }
];

const CourseOverView = ({ setOverview, setCourseId, toggleUpdate }) => {
    const loader = useSelector((state) => state?.currentScreen?.isLoading);
    const { courseId } = useParams();
    const [thumbnail, setThumbnail] = useState(null);
    const [allCategories, setAllCategories] = useState([]);
    const [allSubCategory, setAllSubCategory] = useState([]);
    const [selectedMajor, setSelectedMajor] = useState([]);
    const [selectedSubCat, setSelectedSubCat] = useState([]);
    const [categoryId, setCategoryId] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            console.log('values', values);
            const Id = courseId
                ? (await editCourse(values, courseId),
                  thumbnail && uploadThumbnail(thumbnail, dispatch, courseId))
                : await createCourse(
                      values,
                      setCourseId,
                      toggleUpdate,
                      navigate,
                      thumbnail
                  );
            dispatch;
            console.log('redux', Id);
        }
    });

    const descPlainText = formik.values.description
        ? formik.values.description
              .replace(/<[^>]*>/g, '')
              .replace(/&nbsp;/g, ' ')
              .trim()
        : '';
    const descCharCount = descPlainText.length;
    const descWordCount = descPlainText
        ? descPlainText.split(/\s+/).filter(Boolean).length
        : 0;

    const modules = {
        toolbar: [
            [{ header: '1' }, { header: '2' }, { font: [] }],
            [{ size: [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [
                { list: 'ordered' },
                { list: 'bullet' },
                { indent: '-1' },
                { indent: '+1' }
            ],
            ['link', 'image', 'video'],
            ['clean']
        ],
        clipboard: {
            matchVisual: false
        }
    };

    const handleCategories = (e) => {
        formik.handleChange(e);
        if (e.target.name === 'subCategory') {
            setSelectedSubCat(e.target.value);
        } else if (e.target.name === 'majorCategory') {
            setSelectedMajor(e.target.value);
            const filtreredSub = allCategories.filter(
                (cat) => cat.categoryName === e.target.value
            );
            console.log('Filtered category', filtreredSub[0]);
            setCategoryId(filtreredSub[0]);

            getSubCategoriesById(filtreredSub[0]._id).then((response) =>
                setAllSubCategory(response)
            );
        }
    };

    const uploadFile = (e) => {
        const img = e.target.files[0];
        const data = img.type;
        const result = data.substring(0, 5);
        if (result === 'image') {
            setThumbnail(img);
        } else {
            toast.error('Please upload image');
        }
    };

    const removeThumb = (e) => {
        e.stopPropagation(); // Avoid triggering open file explorer
        setThumbnail('');
    };

    const setupForEditCourse = useCallback(
        (setFieldValues) => {
            store.dispatch(setCurrentScreenLoading(true));
            getAllCategories().then((allFetchedCats) => {
                setAllCategories(allFetchedCats);
                getIndividualCourse(courseId).then((response) => {
                    setSelectedMajor(response?.majorCategory);
                    const requiredMajorCat = allFetchedCats.filter(
                        (category) =>
                            category.categoryName === response?.majorCategory
                    );
                    requiredMajorCat.length > 0 &&
                        getSubCategoriesById(requiredMajorCat[0]?._id).then(
                            (subCategories) => setAllSubCategory(subCategories)
                        );

                    setSelectedSubCat(response.subCategory);
                    setFieldValues(
                        {
                            courseCode: response?.courseCode,
                            courseName: response?.courseName,
                            description: response?.description,
                            majorCategory: response?.majorCategory,
                            subCategory: response?.subCategory,
                            thumbnail: response?.thumbnail
                        },
                        false,
                        store.dispatch(setCurrentScreenLoading(false))
                    );
                });
            });
        },
        [courseId]
    );

    const fetchAllcategories = () => {
        getAllCategories().then((allFetchedCats) => {
            setAllCategories(allFetchedCats);
        });
    };

    useEffect(() => {
        if (courseId) {
            setupForEditCourse(formik.setValues);
        } else {
            fetchAllcategories();
            formik.resetForm({ values: initialValues });
            setThumbnail(null);
            setSelectedMajor([]);
            setSelectedSubCat([]);
            setAllSubCategory([]);
            setCategoryId('');
        }
    }, [courseId, setupForEditCourse]);

    return loader ? (
        <Box
            textAlign="center"
            height="60vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
        >
            <CircularProgress />
        </Box>
    ) : (
        <Box>
            {/* Header Breadcrumb */}
            <Box mb={3} mx={{ sm: 2, xs: 2 }}>
                <BreadCrumb breadItems={breadcrumb} />
            </Box>

            {/* Premium Card container */}
            <Card
                elevation={3}
                sx={{
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    mb: 2
                }}
            >
                {/* Header Details */}
                <Box
                    sx={{
                        p: 3,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <School
                        sx={{
                            fontSize: '2rem',
                            color: theme.palette.secondary.main
                        }}
                    />
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                        >
                            {courseId
                                ? 'Edit Course Details'
                                : 'Create New Course'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Configure core metadata, categories, a detailed
                            description, and landing thumbnail.
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                <CardContent sx={{ p: 4 }}>
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit}>
                            <Box display="flex" flexDirection="column" gap={3}>
                                {/* Double Column Grid: Course Code & Name */}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Course Code"
                                            fullWidth
                                            name="courseCode"
                                            variant="outlined"
                                            value={formik.values.courseCode}
                                            onChange={(e) => {
                                                const trimmedValue =
                                                    e.target.value.replace(
                                                        /^\s+/,
                                                        ''
                                                    );
                                                formik.handleChange(e);
                                                formik.setFieldValue(
                                                    'courseCode',
                                                    trimmedValue
                                                );
                                            }}
                                            error={
                                                formik.touched.courseCode &&
                                                Boolean(
                                                    formik.errors.courseCode
                                                )
                                            }
                                            helperText={
                                                formik.touched.courseCode &&
                                                formik.errors.courseCode
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Course Name"
                                            fullWidth
                                            name="courseName"
                                            variant="outlined"
                                            inputProps={{
                                                maxLength: 50
                                            }}
                                            value={formik.values.courseName}
                                            onChange={(e) => {
                                                const trimmedValue =
                                                    e.target.value.replace(
                                                        /^\s+/,
                                                        ''
                                                    );
                                                formik.handleChange(e);
                                                formik.setFieldValue(
                                                    'courseName',
                                                    trimmedValue
                                                );
                                            }}
                                            error={
                                                formik.touched.courseName &&
                                                Boolean(
                                                    formik.errors.courseName
                                                )
                                            }
                                            helperText={
                                                formik.touched.courseName &&
                                                formik.errors.courseName
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                {/* Double Column Grid: Major Category & Sub Category */}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="category-select-label">
                                                Major Category
                                            </InputLabel>
                                            <Select
                                                labelId="category-select-label"
                                                id="demo-simple-select"
                                                name="majorCategory"
                                                value={selectedMajor}
                                                label="Major Category"
                                                onChange={handleCategories}
                                                error={
                                                    formik.touched
                                                        .majorCategory &&
                                                    Boolean(
                                                        formik.errors
                                                            .majorCategory
                                                    )
                                                }
                                            >
                                                {allCategories?.map((obj) => (
                                                    <MenuItem
                                                        value={obj.categoryName}
                                                        key={obj?._id}
                                                        subcategories={
                                                            obj.subCategories
                                                        }
                                                    >
                                                        {obj.categoryName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.touched.majorCategory &&
                                                Boolean(
                                                    formik.errors.majorCategory
                                                ) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        sx={{ mt: 0.5, pl: 1 }}
                                                    >
                                                        {
                                                            formik.errors
                                                                .majorCategory
                                                        }
                                                    </Typography>
                                                )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="subCategory-label">
                                                Sub Category
                                            </InputLabel>
                                            <Select
                                                labelId="subCategory-label"
                                                id="subCategory"
                                                name="subCategory"
                                                disabled={
                                                    selectedMajor.length === 0
                                                }
                                                value={selectedSubCat}
                                                label="Sub Category"
                                                onChange={handleCategories}
                                                error={
                                                    formik.values
                                                        .majorCategory &&
                                                    formik.touched
                                                        .subCategory &&
                                                    Boolean(
                                                        formik.errors
                                                            .subCategory
                                                    )
                                                }
                                            >
                                                {allSubCategory?.map((obj) => (
                                                    <MenuItem
                                                        key={obj?._id}
                                                        value={obj.categoryName}
                                                        id={obj?._id}
                                                    >
                                                        {obj.categoryName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.values.majorCategory &&
                                                formik.touched.subCategory &&
                                                Boolean(
                                                    formik.errors.subCategory
                                                ) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        sx={{ mt: 0.5, pl: 1 }}
                                                    >
                                                        {
                                                            formik.errors
                                                                .subCategory
                                                        }
                                                    </Typography>
                                                )}
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Beautiful ReactQuill Rich Description area */}
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Course Description
                                    </Typography>
                                    <Box
                                        sx={{
                                            '& .ql-toolbar.ql-snow': {
                                                borderColor:
                                                    formik.touched
                                                        .description &&
                                                    formik.errors.description
                                                        ? `#CA0000 !important`
                                                        : `${theme.palette.divider} !important`
                                            },
                                            '& .ql-container': {
                                                borderBottomLeftRadius: '12px',
                                                borderBottomRightRadius: '12px',
                                                minHeight: '200px',
                                                fontSize: '1rem',
                                                borderColor:
                                                    formik.touched
                                                        .description &&
                                                    formik.errors.description
                                                        ? `#CA0000 !important`
                                                        : `${theme.palette.divider} !important`,
                                                backgroundColor: '#fff'
                                            },
                                            '& .ql-toolbar': {
                                                borderTopLeftRadius: '12px',
                                                borderTopRightRadius: '12px',
                                                borderColor: `${theme.palette.divider} !important`,
                                                backgroundColor:
                                                    theme.palette.grey[100]
                                            }
                                        }}
                                    >
                                        <Field name="description">
                                            {({ field }) => (
                                                <ReactQuill
                                                    placeholder="Describe what students will learn in this course..."
                                                    theme="snow"
                                                    name="description"
                                                    value={field.value}
                                                    onChange={field.onChange(
                                                        field.name
                                                    )}
                                                    modules={modules}
                                                />
                                            )}
                                        </Field>
                                    </Box>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mt: 0.5, px: 0.5 }}
                                    >
                                        <Box>
                                            {formik.touched.description &&
                                                Boolean(
                                                    formik.errors.description
                                                ) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                    >
                                                        {
                                                            formik.errors
                                                                .description
                                                        }
                                                    </Typography>
                                                )}
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            color={
                                                descWordCount > 1500
                                                    ? 'error'
                                                    : 'text.secondary'
                                            }
                                            sx={{ fontWeight: 600, ml: 'auto' }}
                                        >
                                            Characters: {descCharCount} | Words:{' '}
                                            {descWordCount} / 1500 allowed
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Premium Thumbnail Upload Area */}
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Course Cover Thumbnail
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: thumbnail
                                                ? theme.palette.secondary.main
                                                : theme.palette.divider,
                                            borderRadius: '12px',
                                            p: 4,
                                            textAlign: 'center',
                                            backgroundColor: thumbnail
                                                ? 'rgba(82, 146, 221, 0.02)'
                                                : theme.palette.grey[50],
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor:
                                                    theme.palette.secondary
                                                        .main,
                                                backgroundColor:
                                                    'rgba(82, 146, 221, 0.04)'
                                            },
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <input
                                            type="file"
                                            name="myfile"
                                            accept="image/*"
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                opacity: 0,
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            onChange={uploadFile}
                                        />
                                        {thumbnail ? (
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                gap={1.5}
                                            >
                                                <Chip
                                                    label={thumbnail.name}
                                                    onDelete={removeThumb}
                                                    color="secondary"
                                                    sx={{
                                                        fontWeight: 600,
                                                        zIndex: 3,
                                                        position: 'relative'
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Click or drag an image to
                                                    replace this cover
                                                    thumbnail.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <Add
                                                    sx={{
                                                        fontSize: '2.5rem',
                                                        color: theme.palette
                                                            .text.secondary,
                                                        opacity: 0.7
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'text.primary'
                                                    }}
                                                >
                                                    Click or drag cover image to
                                                    upload
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Supports PNG, JPG, JPEG
                                                    formats (Max 5MB)
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {formik.values.thumbnail && !thumbnail && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                pl: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Current Cover:
                                            </Typography>
                                            <Chip
                                                label={formik.values.thumbnail.substring(
                                                    formik.values.thumbnail.lastIndexOf(
                                                        '/'
                                                    ) + 1
                                                )}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Bottom Save Action */}
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    mt={2}
                                >
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        type="submit"
                                        size="large"
                                        sx={{
                                            px: 5,
                                            py: 1.2,
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            boxShadow: theme.shadows[2]
                                        }}
                                    >
                                        {courseId
                                            ? 'Save Changes'
                                            : 'Create Course'}
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </FormikProvider>
                </CardContent>
            </Card>
            <ToastContainer />
        </Box>
    );
};

export default CourseOverView;
